import { RuleType } from 'react-querybuilder';

import {
  CustomPropertySettingsModel,
  DEFAULT_QUERY_OPERATOR_LABELS,
  getCustomPropertyDefinition,
  isPropertyType,
  isQueryBuilderOperator,
  PropertyTypeEnum,
  QUERY_BUILDER_ARRAY_OR_STRING_OPERATORS,
  QUERY_BUILDER_BETWEEN_OPERATORS,
  QueryBuilderField,
} from '@dotfile/shared/domain';

/**
 * Export: RQB -> Natural language
 * Rule Processor extended for custom operators
 * Usage in formatQuery in options.ruleProcessor
 * @doc https://react-querybuilder.js.org/docs/utils/export#rule-processor
 * @see libs/shared/domain/src/lib/utils/json-logic-extended.ts
 * @see https://github.com/react-querybuilder/react-querybuilder/blob/main/packages/react-querybuilder/src/utils/formatQuery/defaultRuleProcessorNL.ts
 */
export const ruleProcessorNaturalLanguageExtended =
  (allFlatFields: QueryBuilderField[]) =>
  (rule: RuleType): string => {
    const field = allFlatFields.find((f) => f.name === rule.field);
    const fieldLabel = field?.label ?? rule.field;

    const operatorLabel = isQueryBuilderOperator(rule.operator)
      ? DEFAULT_QUERY_OPERATOR_LABELS[rule.operator].toLocaleLowerCase()
      : rule.operator;

    const value = !field
      ? rule.value
      : Array.isArray(rule.value)
        ? rule.value
            .map((v) => transformValue(v, rule, field))
            .join(
              // @TODO - E-2270 - Add @total-typescript/ts-reset
              // `as` should not be necessary anymore
              (QUERY_BUILDER_BETWEEN_OPERATORS as string[]).includes(
                rule.operator,
              )
                ? ' and '
                : ', ',
            )
        : transformValue(rule.value, rule, field);

    return `${fieldLabel} ${operatorLabel} ${value}`.trim();
  };

function transformValue(
  value: unknown,
  rule: RuleType,
  field: QueryBuilderField,
): string {
  if (value === null) return '';

  if (
    // Should not happen since the sub-properties are exposed in logics
    field.property.type === PropertyTypeEnum.banking_information ||
    field.property.type === PropertyTypeEnum.address ||
    // Will display the the code
    field.property.type === PropertyTypeEnum.entity_legal_form ||
    // Should not happen since unknown properties are not supposed to be used in logics
    field.property.type === PropertyTypeEnum.unknown
  ) {
    return `${value}`;
  }

  if (field.property.type === PropertyTypeEnum.classifications) {
    if (typeof value === 'string' && value.includes('__')) {
      const [type, code] = value.split('__');
      return `${type} ${code}`;
    }
    return `${value}`;
  }

  const propertyDefinition = getCustomPropertyDefinition(field.property.type);
  if (
    (propertyDefinition.multiValues ||
      (
        [
          ...QUERY_BUILDER_BETWEEN_OPERATORS,
          ...QUERY_BUILDER_ARRAY_OR_STRING_OPERATORS,
          // @TODO - E-2270 - Add @total-typescript/ts-reset
          // `as` should not be necessary anymore
        ] as string[]
      ).includes(rule.operator)) &&
    typeof value === 'string' &&
    value.includes(',')
  ) {
    return value
      .split(',')
      .map((v) => transformValue(v, rule, field))
      .join(
        // @TODO - E-2270 - Add @total-typescript/ts-reset
        // `as` should not be necessary anymore
        (QUERY_BUILDER_BETWEEN_OPERATORS as string[]).includes(rule.operator)
          ? ' and '
          : ', ',
      );
  }

  return propertyDefinition.formatValue(
    {
      value: `${value}`,
      customProperty: {
        settings: field.property.settings as CustomPropertySettingsModel,
      },

      // This is sub-optimal but the customPropertyDefinition pre-date the introduction of Property
      // Ideally we could merge the CustomPropertyDefinition into the Property definition and handle
      // the formatting there, without a specific logic for the choices here
      customPropertyOption: isPropertyType(PropertyTypeEnum.choices)(
        field.property,
      )
        ? (field.property.settings?.options?.find((v) => v.key === value) ??
          null)
        : null,
    },
    {
      // No options needed
    },
  );
}
